
import {catchError} from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable ,  Subject } from 'rxjs';
import { ExceptionService, UtilsService } from './../../../xform-compat';
import { CompanySpace, Compliance, Thesis, ThesisHistory, Draft } from '../../../shared/models';
import { ContentService } from '../../services';

/**
 * Data service to interact with the Thesis API
 */
@Injectable()
export class ThesisService {
  private thesisUpdate: Subject<any>;

  constructor(
    private http: HttpClient,
    private utilsService: UtilsService,
    private exceptionService: ExceptionService,
    private contentService: ContentService
  ) {
    this.thesisUpdate = new Subject<any>();
  }

  /**
   * Sends a message to the subscribed object on the component
   * @param message
   */
  public sendMessage(message: string) {
    this.thesisUpdate.next({ text: message });
  }

  /**
   * Observable to listen when a message is sent
   */
  public getMessage(): Observable<any> {
    return this.thesisUpdate.asObservable();
  }

  /**
   * Gets the company thesis
   * @param {number} spaceId
   * @param params
   * @returns {Observable<Thesis>}
   */
  public getCompanyThesis (spaceId?: number, params?: any): Observable<Array<CompanySpace>> {
    const url = `/api/rms/spaces/${spaceId}/thesis`;
    const httpParams: HttpParams = this.utilsService.generateParams(params);
    return this.http.get(url, {params: httpParams}).pipe(catchError(ex => this.exceptionService.catchBadResponse(ex)));
  }

  /**
   * Gets the thesis Draft for the current user
   * @param {number} spaceId
   * @returns {Observable<Thesis>}
   */
  public getDraft (spaceId?: number): Observable<Thesis> {
    const url = `/api/rms/spaces/${spaceId}/thesis/draft/me`;
    return this.http.get(url).pipe(catchError(ex => this.exceptionService.catchBadResponse(ex)));
  }

  /**
   * Saves the thesis as draft for the current user
   * @param {number} spaceId
   * @param thesisContent: body with the content string and optional attachments
   * @returns {Observable<CompanySpace>}
   */
  public saveAsDraft (spaceId: number, thesisContent: {content: string, attachments?: Array<number>}): Observable<Draft> {
    const url = `/api/rms/spaces/${spaceId}/thesis/draft`;
    thesisContent.content = this.contentService.formatInlineImageContentRemove(thesisContent.content);
    return this.http.post(url, thesisContent).pipe(
      catchError(ex => this.exceptionService.catchBadResponse(ex)));
  }

  /**
   * Fetches the history list of previous thesis after edit
   * @param {number} spaceId
   * @returns {Observable<Array<ThesisHistory>>}
   */
  public getHistory (spaceId: number): Observable<Array<ThesisHistory>> {
    const url = `/api/rms/spaces/${spaceId}/thesis/history`;
    return this.contentService.formatInlineImageObservableThesisArray(this.http.get(url)
      .pipe(catchError(ex => this.exceptionService.catchBadResponse(ex))));
  }

  /**
   * Saves the thesis
   * @param {number} spaceId
   * @param thesisContent: The thesis body content and optiona attachments
   * @returns {Observable<CompanySpace>}
   */
  public postThesis (spaceId: number, thesisContent: {content: string, attachments?: Array<number>}): Observable<CompanySpace> {
    const url = `/api/rms/spaces/${spaceId}/thesis`;
    thesisContent.content = this.contentService.formatInlineImageContentRemove(thesisContent.content);
    return this.http.post(url, thesisContent).pipe(
      catchError(ex => this.exceptionService.catchBadResponse(ex)));
  }

  /**
   * If Draft exists, deletes the draft from the thesis
   * @param {number} spaceId
   * @returns {Observable<any>}
   */
  public discardThesis (spaceId: number): Observable<any> {
    return this.http.delete(`/api/rms/spaces/${spaceId}/thesis/draft/me`).pipe(catchError(ex => this.exceptionService.catchBadResponse(ex)));
  }

  /**
   * Saves the MNPI review
   * @param {number} spaceId
   * @param thesisId
   * @param {{complianceReviewComment: string}} body
   * @returns {Observable<Compliance>}
   */
  public reviewThesisCompliance (spaceId: number, thesisId: number,  body: {complianceReviewComment: string}): Observable<Compliance> {
    return this.http.post(`/api/rms/spaces/${spaceId}/thesis/${thesisId}/review`, body)
      .pipe(catchError(ex => this.exceptionService.catchBadResponse(ex)));
  }

  /**
   * Updates the object references for the thesis
   * @param spaceId
   * @param body
   */
  public updateThesisReferences (spaceId: number, body: any): Observable<Thesis> {
    return this.http.put(`/api/rms/spaces/${spaceId}/thesis/references`, body).pipe(catchError(ex => this.exceptionService.catchBadResponse(ex)));
  }

}
